import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CloudQueue,
  Security,
  DataUsage,
  Business,
  TrendingUp,
  Support,
  Speed,
  Analytics,
  Code,
  Storage,
  Api,
  ExpandMore
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Applications = () => {
  const services = [
    {
      icon: <Code sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Application Modernization',
      description: 'Unlock the value of your digital business with comprehensive application transformation services.',
      features: ['Legacy System Migration', 'Cloud-Native Development', 'Microservices Architecture'],
      benefits: ['Improved Performance', 'Reduced Costs', 'Enhanced Scalability'],
      link: '/applications/modernization'
    },
    {
      icon: <CloudQueue sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Cloud Migration',
      description: 'Migrate applications for enhanced agility and superior user experience across cloud platforms.',
      features: ['Multi-Cloud Strategy', 'Data Migration', 'Application Portability'],
      benefits: ['Faster Deployment', 'Cost Optimization', 'Global Accessibility'],
      link: '/applications/cloud-migration'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Application Security',
      description: 'Strengthen security with industry-leading cyber resilience solutions for your applications.',
      features: ['Security Assessment', 'Threat Detection', 'Compliance Management'],
      benefits: ['Enhanced Protection', 'Regulatory Compliance', 'Risk Mitigation'],
      link: '/applications/security'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'SAP Application Services',
      description: 'Harness the full potential of SAP cloud ERP solutions with expert implementation and management.',
      features: ['SAP S/4HANA Migration', 'Cloud Integration', 'Process Optimization'],
      benefits: ['Streamlined Operations', 'Real-time Analytics', 'Improved Efficiency'],
      link: '/applications/sap-services'
    },
    {
      icon: <Api sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Oracle Application Services',
      description: 'Reduce costs and increase agility with comprehensive Oracle application management.',
      features: ['Database Optimization', 'Application Tuning', 'Performance Monitoring'],
      benefits: ['Cost Reduction', 'Improved Performance', 'Better Reliability'],
      link: '/applications/oracle-services'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'DevSecOps Services',
      description: 'Deliver secure, intelligent applications with integrated development and security practices.',
      features: ['CI/CD Pipeline', 'Automated Testing', 'Security Integration'],
      benefits: ['Faster Delivery', 'Higher Quality', 'Reduced Risk'],
      link: '/applications/devops'
    }
  ];

  const customerStories = [
    {
      title: 'Arizona Motor Vehicle Division',
      description: 'Shaping the future of motor vehicle services and elevating driver experiences',
      outcome: 'Arizona MVD achieves its vision of delivering fast, efficient, and citizen-friendly services by completely re-architecting back-end and front-end processes and rebuilding applications.',
      industry: 'Government',
      services: ['Application Modernization', 'Cloud Migration']
    },
    {
      title: 'Grupo Romero',
      description: 'Enhancing efficiency and agility with cloud and SAP enterprise resource planning upgrades',
      outcome: 'Excellia - Grupo Romero, a multi-industry conglomerate, wanted to decentralize their SAP solution to provide greater flexibility for their individual businesses to grow independently.',
      industry: 'Manufacturing',
      services: ['SAP Services', 'Cloud Migration']
    }
  ];

  const faqs = [
    {
      question: 'What are application services?',
      answer: 'Application services are a set of services – including consulting, planning, architecture, design, development, implementation, management, and optimization – delivered to enhance the performance, reliability, scalability and security of enterprise applications. Application services are the backbone of modern digital infrastructure, enabling enterprises and end-users to perform tasks efficiently and effectively.'
    },
    {
      question: 'How do cloud application services differ from traditional application services?',
      answer: 'Cloud-based application services offer a more affordable, scalable, and adaptable solution compared to traditional applications. By eliminating the need for maintaining physical servers, introducing a pay-as-you-go payment model, and utilizing microservices to expedite updates, they provide a modern and efficient solution for contemporary businesses.'
    },
    {
      question: 'How do application services help in digital transformation?',
      answer: 'Application services are key to digital transformation, helping organizations improve IT infrastructure, efficiency, and customer experiences. They enable transition to cloud-based solutions, offering scalability and cost savings, and support the development of user-centric applications, enhancing interactions and streamlining processes.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Application Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Transform your applications to enable business agility and innovation at scale
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 4
              }}
            >
              Unlock the full value of your applications
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              To drive business agility and deliver improved user experiences, organizations must implement a flexible strategy for transforming their applications and data. We help you transform your complex applications portfolio, boost performance and availability, improve security, and maintain application currency.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Application Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  component={Link}
                  to={service.link}
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                      textDecoration: 'none',
                      color: 'inherit'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Key Features:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Benefits:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.benefits.map((benefit, idx) => (
                          <Chip
                            key={idx}
                            label={benefit}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn More →
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Stories Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {story.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      {story.description}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {story.outcome}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={story.industry}
                        sx={{
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                      <Stack direction="row" spacing={1}>
                        {story.services.map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Ready to Transform Your Applications?
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get a 30-minute, no-cost strategy session with an Application Services expert
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request a Consultation
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Applications;
